import { Action } from "redux";
import { APIPartner, PartnerPatch } from "../../server/types/index.js";
import { ThunkAction } from "../types/index.js";
import { fetch } from "../utils/utils.js";
import { showAlert } from "./Alerts.js";
import { isActionType } from "./utils.js";

const ActionTypeKeys = [
  "GET_PARTNERS_SUCCESS",
  "GET_PARTNERS_START",
  "GET_PARTNERS_ERROR",
  "PATCH_PARTNER_SUCCESS",
  "PATCH_PARTNER_ERROR",
] as const;

type ActionType = (typeof ActionTypeKeys)[number];
type ActionBase<T extends ActionType> = Action<T>;

type GetPartnersSuccess = ActionBase<"GET_PARTNERS_SUCCESS"> & {
  partners: APIPartner[];
};
type GetPartnersStart = ActionBase<"GET_PARTNERS_START">;
type GetPartnersError = ActionBase<"GET_PARTNERS_ERROR"> & { error: Error };
type PatchPartnerSuccess = ActionBase<"PATCH_PARTNER_SUCCESS"> & {
  partner: APIPartner;
};
type PatchPartnerError = ActionBase<"PATCH_PARTNER_ERROR"> & { error: Error };

export type PartnersAction =
  | GetPartnersSuccess
  | GetPartnersStart
  | GetPartnersError
  | PatchPartnerSuccess
  | PatchPartnerError;

export const isPartnersAction = (action: Action): action is PartnersAction =>
  isActionType(action, ActionTypeKeys);

export const getPartners = (siteId: string): ThunkAction<Promise<void>> => {
  return (dispatch, getState) => {
    if (getState().loadStates.partners !== "unloaded") {
      return Promise.resolve();
    }

    dispatch(getPartnersStart());
    return fetch({ dispatch })
      .get(`sites/${siteId}/partners`)
      .then((res) => {
        dispatch(getPartnersSuccess(res.data));
      })
      .catch((error) => {
        dispatch(getPartnersError(error));
        throw error;
      });
  };
};

export const getPartnersSuccess = (
  partners: APIPartner[],
): GetPartnersSuccess => ({ type: "GET_PARTNERS_SUCCESS", partners });

const getPartnersError = (error: Error): GetPartnersError => ({
  type: "GET_PARTNERS_ERROR",
  error,
});

const getPartnersStart = (): GetPartnersStart => ({
  type: "GET_PARTNERS_START",
});

export const patchPartner = (
  siteId: string,
  partnerId: string,
  body: PartnerPatch,
): ThunkAction<void> => {
  return (dispatch) => {
    return fetch({ dispatch })
      .patch(`sites/${siteId}/partners/${partnerId}`, body)
      .then((res) => {
        dispatch(patchPartnerSuccess(res.data));
      })
      .catch((error) => {
        dispatch(
          showAlert(
            `Der Partner "${partnerId}" konnte nicht aktualisiert werden!`,
          ),
        );
        throw error;
      });
  };
};

const patchPartnerSuccess = (partner: APIPartner): PatchPartnerSuccess => ({
  type: "PATCH_PARTNER_SUCCESS",
  partner,
});
